import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoBlack from '../assets/logoblack.png';

function parseAddons(order) {
  if (!order) return [];
  if (Array.isArray(order.addons)) return order.addons;
  if (!order.addons_json) return [];

  try {
    const decoded = JSON.parse(order.addons_json);
    return Array.isArray(decoded) ? decoded : [];
  } catch (_) {
    return [];
  }
}

function formatAmount(value) {
  return `INR ${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function textRight(doc, text, x, y) {
  const w = doc.getTextWidth(String(text));
  doc.text(String(text), x - w, y);
}

function statusMeta(statusRaw) {
  const status = String(statusRaw || '').toLowerCase();
  if (status === 'paid' || status === 'captured' || status === 'delivered') {
    return { label: 'PAID / CONFIRMED', bg: [233, 247, 239], border: [188, 224, 198], text: [18, 120, 70] };
  }
  if (status === 'shipped') {
    return { label: 'SHIPPED', bg: [233, 241, 255], border: [179, 205, 245], text: [36, 92, 179] };
  }
  if (status === 'failed' || status === 'cancelled') {
    return { label: 'FAILED', bg: [255, 239, 239], border: [245, 197, 197], text: [171, 49, 49] };
  }
  return { label: 'PENDING', bg: [255, 246, 231], border: [244, 217, 171], text: [144, 96, 23] };
}

async function loadImageDataUrl(src) {
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return null;
  }
}

export async function downloadInvoicePdf(orderBundle) {
  if (!orderBundle || !orderBundle.order) return;

  const order = orderBundle.order;
  const items = Array.isArray(orderBundle.items) ? orderBundle.items : [];
  const shipping = orderBundle.shipping || {};
  const addons = parseAddons(order);
  const payment = orderBundle.payment || null;

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const contentWidth = pageWidth - (marginX * 2);

  const subtotal = Number(order.subtotal_amount || 0);
  const addonsTotal = Number(order.addons_total || 0);
  const shippingAmount = Number(order.shipping_amount || 0);
  const totalAmount = Number(order.total_amount || 0);

  const invoiceNo = `INV-${String(order.id || 0).padStart(6, '0')}`;
  const invoiceDate = formatDateTime(order.created_at);
  const status = statusMeta(payment?.status || order.status);
  const logoData = await loadImageDataUrl(logoBlack);

  doc.setTextColor(25, 30, 42);

  // Header
  if (logoData) {
    doc.addImage(logoData, 'PNG', marginX, 32, 40, 40);
  }
  const brandX = logoData ? (marginX + 50) : marginX;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('KAALEEN HAUS', brandX, 46);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 106, 117);
  doc.text('Luxury Handcrafted Rugs', brandX, 61);
  doc.text('GSTIN: 27AABCU9603R1ZX', brandX, 74);

  doc.setFontSize(9);
  textRight(doc, 'support@kaaleenhaus.com', pageWidth - marginX, 50);
  textRight(doc, window.location?.origin || 'https://kaaleenhaus.com', pageWidth - marginX, 63);

  doc.setDrawColor(228, 232, 238);
  doc.line(marginX, 86, pageWidth - marginX, 86);

  // Invoice heading
  doc.setTextColor(33, 37, 41);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(25);
  doc.text('TAX INVOICE', marginX, 122);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(86, 92, 102);
  doc.text(`Invoice No: ${invoiceNo}`, marginX, 140);
  doc.text(`Date: ${invoiceDate}`, marginX, 154);

  doc.setDrawColor(...status.border);
  doc.setFillColor(...status.bg);
  doc.roundedRect(marginX, 164, 124, 18, 4, 4, 'FD');
  doc.setTextColor(...status.text);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(status.label, marginX + 8, 176);

  doc.setDrawColor(232, 236, 242);
  doc.line(marginX, 198, pageWidth - marginX, 198);

  // Billing section
  doc.setTextColor(108, 113, 123);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('BILLED TO', marginX, 224);

  doc.setTextColor(31, 37, 45);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(order.guest_name || 'Customer', marginX, 246);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(88, 95, 105);
  doc.text(order.guest_email || '-', marginX, 262);
  doc.text(`Mobile: ${order.guest_phone || '-'}`, marginX, 276);

  // Shipping / order meta card
  const cardX = marginX;
  const cardY = 292;
  const cardW = contentWidth;
  const shippingLines = [
    shipping.line1,
    shipping.line2,
    `${shipping.city || ''} ${shipping.state || ''}`.trim(),
    `${shipping.postal_code || ''} ${shipping.country || ''}`.trim(),
  ].filter(Boolean);
  const shippingText = shippingLines.length > 0 ? shippingLines.join(', ') : 'Address not available';
  const rightBlockX = cardX + 296;
  const rightBlockWidth = cardX + cardW - rightBlockX - 14;
  const shippingWrapped = doc.splitTextToSize(`Shipping: ${shippingText}`, Math.max(120, rightBlockWidth));
  const paymentRefWrapped = doc.splitTextToSize(`Payment Ref: ${payment?.provider_payment_id || '-'}`, 210);
  const rightLines = shippingWrapped.length + paymentRefWrapped.length;
  const cardH = Math.max(84, 36 + (rightLines * 11));
  doc.setDrawColor(230, 233, 239);
  doc.setFillColor(250, 251, 253);
  doc.roundedRect(cardX, cardY, cardW, cardH, 6, 6, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(90, 96, 107);
  doc.text('ORDER SUMMARY', cardX + 14, cardY + 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(40, 45, 52);
  doc.text(`Order ID: ${order.order_public_id || order.id || '-'}`, cardX + 14, cardY + 39);
  doc.text(`Payment Provider: ${payment?.provider || order.payment_provider || 'razorpay'}`, cardX + 14, cardY + 55);
  const payRefForCard = payment?.provider_payment_id ? `Payment Ref: ${payment.provider_payment_id}` : 'Payment Ref: -';
  const payRefCardWrapped = doc.splitTextToSize(payRefForCard, 220);
  doc.text(payRefCardWrapped, cardX + 14, cardY + 71);

  doc.setTextColor(78, 84, 93);
  doc.text(shippingWrapped, rightBlockX, cardY + 39);

  // Items table
  autoTable(doc, {
    startY: cardY + cardH + 18,
    head: [['DESCRIPTION', 'SIZE', 'QTY', 'UNIT PRICE', 'TOTAL']],
    body: items.map((it) => {
      const unit = Number(it.locked_price || 0);
      const qty = Number(it.quantity || 0);
      return [
        it.product_name || 'Product',
        it.selected_size || '-',
        String(qty),
        formatAmount(unit),
        formatAmount(unit * qty),
      ];
    }),
    theme: 'plain',
    styles: {
      fontSize: 10,
      cellPadding: 8,
      textColor: [39, 44, 52],
      lineColor: [232, 235, 240],
      lineWidth: 0.6,
      valign: 'middle',
    },
    headStyles: {
      textColor: [96, 102, 112],
      fontStyle: 'bold',
      fillColor: [255, 255, 255],
    },
    columnStyles: {
      0: { cellWidth: 190 },
      1: { cellWidth: 70 },
      2: { halign: 'center', cellWidth: 44 },
      3: { halign: 'right', cellWidth: 95 },
      4: { halign: 'right', cellWidth: 95 },
    },
    didParseCell: (data) => {
      if (data.section === 'head') {
        data.cell.styles.lineWidth = { top: 0, right: 0, bottom: 1, left: 0 };
      } else {
        data.cell.styles.lineWidth = { top: 0, right: 0, bottom: 0.6, left: 0 };
      }
    },
  });

  let blockY = (doc.lastAutoTable?.finalY || 490) + 18;
  if (blockY > 628) {
    doc.addPage();
    blockY = 70;
  }

  // Totals block
  const totalsX = pageWidth - marginX - 265;
  const totalsW = 265;
  doc.setDrawColor(232, 236, 242);
  const totalsH = 116;
  if (blockY + totalsH > pageHeight - 110) {
    doc.addPage();
    blockY = 70;
  }
  doc.roundedRect(totalsX, blockY, totalsW, totalsH, 6, 6);

  const rows = [
    ['Subtotal', formatAmount(subtotal)],
    ['Additional Services', addonsTotal > 0 ? formatAmount(addonsTotal) : 'INR 0.00'],
    ['Delivery Charges', shippingAmount > 0 ? formatAmount(shippingAmount) : 'FREE'],
  ];

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(67, 73, 83);
  let rowY = blockY + 24;
  rows.forEach(([label, value]) => {
    doc.text(label, totalsX + 14, rowY);
    textRight(doc, value, totalsX + totalsW - 14, rowY);
    rowY += 20;
  });

  if (addons.length > 0) {
    doc.setFontSize(8.5);
    doc.setTextColor(116, 121, 130);
    const addonsLine = addons.map((a) => a.label || a.code || 'Addon').join(', ');
    const addonWrapped = doc.splitTextToSize(`Includes: ${addonsLine}`, totalsW - 28);
    doc.text(addonWrapped, totalsX + 14, rowY - 6);
  }

  doc.setDrawColor(228, 233, 239);
  doc.line(totalsX + 14, blockY + 78, totalsX + totalsW - 14, blockY + 78);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(173, 125, 18);
  doc.text('Total Amount', totalsX + 14, blockY + 102);
  textRight(doc, formatAmount(totalAmount), totalsX + totalsW - 14, blockY + 102);

  // Terms and footer
  let termsY = blockY + 144;
  if (termsY > pageHeight - 72) {
    doc.addPage();
    termsY = 70;
  }

  doc.setDrawColor(232, 236, 242);
  doc.line(marginX, termsY - 14, pageWidth - marginX, termsY - 14);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(110, 115, 124);
  doc.text('TERMS & CONDITIONS', marginX, termsY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(104, 110, 120);
  const terms = 'This is a computer-generated invoice and does not require a physical signature. For invoice queries, contact support@kaaleenhaus.com.';
  const termsLines = doc.splitTextToSize(terms, contentWidth);
  doc.text(termsLines, marginX, termsY + 14);

  doc.setFontSize(8.2);
  const paymentRefFooter = doc.splitTextToSize(`Payment Reference: ${payment?.provider_payment_id || '-'}`, contentWidth - 160);
  doc.text(paymentRefFooter, marginX, termsY + 40);
  textRight(doc, `${window.location?.origin || 'https://kaaleenhaus.com/invoice'}  |  1/1`, pageWidth - marginX, termsY + 40);

  doc.save(`${order.order_public_id || 'invoice'}.pdf`);
}
